import React, { useState, useEffect } from "react";
import CategoryTable from "../../components/Product/CategoryTable";
import api from "../../../utilis/api";
import { toast } from "react-toastify";

const ITEMS_PER_PAGE = 10;

const Category = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [form, setForm] = useState({
    name: "",
    status: "Enabled",
  });

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      // For now, we'll use dummy data since the categories API might not exist yet
      // In a real implementation, you'd call: const res = await api.get("/categories");
      const dummyCategories = Array.from({ length: 12 }, (_, i) => ({
        _id: `cat_${i + 1}`,
        name: ["Men's Fragrance", "Women's Perfume", "Unisex Scents", "Luxury Collection", "Everyday Wear", "Special Occasion", "Gift Sets", "Travel Size", "Body Mists", "Home Fragrance", "Candles", "Diffusers"][i],
        status: ["Enabled", "Disabled"][Math.floor(Math.random() * 2)],
        createdAt: new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString(),
      }));

      setCategories(dummyCategories);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to load categories");
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleAdd = async () => {
    if (!form.name.trim()) {
      toast.error("Please enter a category name");
      return;
    }

    try {
      // In a real implementation: await api.post("/categories", form);
      const newCategory = {
        _id: `cat_${Date.now()}`,
        ...form,
        createdAt: new Date().toISOString(),
      };

      setCategories([newCategory, ...categories]);
      setIsAddOpen(false);
      setForm({ name: "", status: "Enabled" });
      toast.success("Category added successfully");
    } catch (error) {
      toast.error("Failed to add category");
    }
  };

  const handleEdit = (category) => {
    setCurrentCategory(category);
    setForm({ name: category.name, status: category.status });
    setIsEditOpen(true);
  };

  const handleUpdate = async () => {
    if (!form.name.trim()) {
      toast.error("Please enter a category name");
      return;
    }

    try {
      // In a real implementation: await api.put(`/categories/${currentCategory._id}`, form);
      const updatedCategories = categories.map(cat =>
        cat._id === currentCategory._id ? { ...cat, ...form } : cat
      );

      setCategories(updatedCategories);
      setIsEditOpen(false);
      setCurrentCategory(null);
      setForm({ name: "", status: "Enabled" });
      toast.success("Category updated successfully");
    } catch (error) {
      toast.error("Failed to update category");
    }
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm("Are you sure you want to delete this category?")) return;

    try {
      // In a real implementation: await api.delete(`/categories/${categoryId}`);
      setCategories(categories.filter(cat => cat._id !== categoryId));
      toast.success("Category deleted successfully");
    } catch (error) {
      toast.error("Failed to delete category");
    }
  };

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const pagedData = categories.slice(startIndex, endIndex);

  return (
    <div className="flex flex-col w-full gap-4">
      {/* Header */}
      <div className="bg-[#121218] p-4 border border-white/10 rounded-lg">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="font-bold text-xl text-white">Manage Product Categories</h1>
            <p className="text-gray-400 text-sm mt-1">
              {loading ? "Loading categories..." : `Total ${categories.length} categories`}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setIsAddOpen(true)}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition duration-200"
            >
              Add Category
            </button>
            <button
              onClick={fetchCategories}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition duration-200"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="bg-[#121218] border border-white/10 rounded-lg p-4">
        {loading ? (
          <div className="text-center py-8">
            <div className="text-gray-400">Loading categories...</div>
          </div>
        ) : (
          <CategoryTable
            data={pagedData}
            currentPage={currentPage}
            totalPages={Math.ceil(categories.length / ITEMS_PER_PAGE)}
            onPageChange={handlePageChange}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        )}
      </div>

      {/* Add Category Modal */}
      {isAddOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#121218] border border-white/10 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-white mb-4">Add New Category</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Category Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-[#1a1a1f] border border-white/20 rounded-md text-white placeholder-gray-400"
                  placeholder="Enter category name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Status
                </label>
                <select
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-[#1a1a1f] border border-white/20 rounded-md text-white"
                >
                  <option value="Enabled">Enabled</option>
                  <option value="Disabled">Disabled</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setIsAddOpen(false);
                  setForm({ name: "", status: "Enabled" });
                }}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleAdd}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition duration-200"
              >
                Add Category
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Category Modal */}
      {isEditOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#121218] border border-white/10 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-white mb-4">Edit Category</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Category Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-[#1a1a1f] border border-white/20 rounded-md text-white placeholder-gray-400"
                  placeholder="Enter category name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Status
                </label>
                <select
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-[#1a1a1f] border border-white/20 rounded-md text-white"
                >
                  <option value="Enabled">Enabled</option>
                  <option value="Disabled">Disabled</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setIsEditOpen(false);
                  setCurrentCategory(null);
                  setForm({ name: "", status: "Enabled" });
                }}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition duration-200"
              >
                Update Category
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Category;
